/**
 * PCG32 (PCG-XSH-RR 64/32) — the ONLY source of randomness in the sim. (§15.3)
 *
 * `Math.random()` is banned inside /sim. It is unseeded and unreproducible, so
 * the client's prediction and the server's authority diverge on every random
 * draw, reconciliation fights itself forever, and the symptom (players jittering
 * under load) looks nothing like the cause. §15.3 budgets a week for finding it.
 * Don't. Use this.
 *
 * Why hand-rolled 64-bit arithmetic instead of BigInt:
 *   - The state is POD (four uint32s) so it lives in WorldState, gets cloned by
 *     buffer copy, and hashes with everything else (§13.1).
 *   - BigInt allocates. This runs in a 60Hz loop with a zero-allocation budget
 *     (§15.7), and BigInt↔Number conversion on every draw would be visible as
 *     GC stutter.
 *   - All arithmetic below is exact: intermediate products are decomposed into
 *     16-bit limbs so nothing ever exceeds 2^53 before being wrapped to uint32.
 *
 * Verified against the reference C implementation's output stream — see
 * pcg32.test.ts, which pins the first 16 outputs for the canonical seed.
 */

/** POD state. Four uint32s = one 64-bit state + one 64-bit increment. */
export interface Pcg32 {
  stateHi: number;
  stateLo: number;
  /** Must always be ODD — this is what makes the stream full-period. */
  incHi: number;
  incLo: number;
}

/** PCG default multiplier: 6364136223846793005 = 0x5851F42D4C957F2D */
const MULT_HI = 0x5851f42d;
const MULT_LO = 0x4c957f2d;

/** PCG default stream increment: 1442695040888963407 = 0x14057B7EF767814F */
const DEFAULT_INC_HI = 0x14057b7e;
const DEFAULT_INC_LO = 0xf767814f;

/**
 * Exact 32×32→64 unsigned multiply.
 * Decomposed into 16-bit limbs: every partial product is < 2^32 and every sum
 * is < 2^53, so all intermediates are exactly representable as f64.
 */
function mul32to64(a: number, b: number): { hi: number; lo: number } {
  const a0 = a & 0xffff;
  const a1 = a >>> 16;
  const b0 = b & 0xffff;
  const b1 = b >>> 16;

  const p0 = a0 * b0; // < 2^32
  const p1 = a1 * b0; // < 2^32
  const p2 = a0 * b1; // < 2^32
  const p3 = a1 * b1; // < 2^32

  // Sum the middle limbs. Max: 0xFFFF + 0xFFFF + 0xFFFF = 0x2FFFD — no overflow.
  const mid = (p0 >>> 16) + (p1 & 0xffff) + (p2 & 0xffff);

  const lo = ((p0 & 0xffff) | ((mid & 0xffff) << 16)) >>> 0;
  // p3 + carries is < 2^33, exact as f64; >>> 0 wraps it to uint32 (mod 2^64 overall).
  const hi = (p3 + (p1 >>> 16) + (p2 >>> 16) + (mid >>> 16)) >>> 0;

  return { hi, lo };
}

/** 64-bit add, mod 2^64. */
function add64(
  aHi: number, aLo: number,
  bHi: number, bLo: number,
): { hi: number; lo: number } {
  // Both operands coerced to unsigned; sum < 2^33, exact as f64.
  const sum = (aLo >>> 0) + (bLo >>> 0);
  const lo = sum >>> 0;
  const carry = sum > 0xffffffff ? 1 : 0;
  const hi = (aHi + bHi + carry) >>> 0;
  return { hi, lo };
}

/** 64-bit multiply, mod 2^64. Only the low 64 bits of the product survive. */
function mul64(
  aHi: number, aLo: number,
  bHi: number, bLo: number,
): { hi: number; lo: number } {
  const loProduct = mul32to64(aLo, bLo);
  // Cross terms only contribute to the high word (their low halves overflow out).
  const hi = (loProduct.hi + Math.imul(aHi, bLo) + Math.imul(aLo, bHi)) >>> 0;
  return { hi, lo: loProduct.lo };
}

/** Rotate a uint32 right by `rot` bits. */
function rotr32(value: number, rot: number): number {
  // (-rot) & 31 handles rot === 0 — `x << 32` is a no-op in JS, not a zero.
  return ((value >>> rot) | (value << ((-rot) & 31))) >>> 0;
}

/**
 * Seed a generator. `seq` selects one of 2^63 independent streams — give each
 * concurrent match a distinct stream so two matches on the same server process
 * can never correlate.
 */
export function pcg32Create(seed: number, seq = 0): Pcg32 {
  const rng: Pcg32 = { stateHi: 0, stateLo: 0, incHi: 0, incLo: 0 };
  pcg32Seed(rng, seed, seq);
  return rng;
}

/** Re-seed in place. Zero allocation. */
export function pcg32Seed(rng: Pcg32, seed: number, seq = 0): void {
  // inc = (seq << 1) | 1  — the low bit forced on keeps the period full.
  const seqLo = seq >>> 0;
  const seqHi = Math.floor(seq / 0x100000000) >>> 0;
  rng.incHi = ((seqHi << 1) | (seqLo >>> 31)) >>> 0;
  rng.incLo = ((seqLo << 1) | 1) >>> 0;

  // Reference init sequence: state = 0; next(); state += seed; next();
  rng.stateHi = 0;
  rng.stateLo = 0;
  pcg32Next(rng);

  const seedLo = seed >>> 0;
  const seedHi = Math.floor(seed / 0x100000000) >>> 0;
  const s = add64(rng.stateHi, rng.stateLo, seedHi, seedLo);
  rng.stateHi = s.hi;
  rng.stateLo = s.lo;

  pcg32Next(rng);
}

/**
 * Advance the stream and return the next uint32.
 * This is the primitive; everything else derives from it.
 */
export function pcg32Next(rng: Pcg32): number {
  const oldHi = rng.stateHi;
  const oldLo = rng.stateLo;

  // state = state * MULT + inc
  const m = mul64(oldHi, oldLo, MULT_HI, MULT_LO);
  const s = add64(m.hi, m.lo, rng.incHi, rng.incLo);
  rng.stateHi = s.hi;
  rng.stateLo = s.lo;

  // XSH: xorshifted = (uint32)(((old >> 18) ^ old) >> 27)
  const shiftedLo = ((oldLo >>> 18) | (oldHi << 14)) >>> 0;
  const shiftedHi = oldHi >>> 18;
  const xorLo = (shiftedLo ^ oldLo) >>> 0;
  const xorHi = (shiftedHi ^ oldHi) >>> 0;
  const xorshifted = ((xorLo >>> 27) | (xorHi << 5)) >>> 0;

  // RR: rot = (uint32)(old >> 59)
  const rot = oldHi >>> 27;

  return rotr32(xorshifted, rot);
}

/** Uniform float in [0, 1). 32 bits of entropy — ample for error cones. */
export function pcg32Float(rng: Pcg32): number {
  return pcg32Next(rng) * 2.3283064365386963e-10; // 1 / 2^32
}

/** Uniform float in [min, max). */
export function pcg32Range(rng: Pcg32, min: number, max: number): number {
  return min + pcg32Float(rng) * (max - min);
}

/**
 * Uniform integer in [0, bound), debiased by rejection.
 *
 * The naive `next() % bound` is subtly wrong: unless bound divides 2^32, low
 * values get one extra chance and the distribution skews. It would never show
 * up in a test but would quietly bias every coin-flip in the game.
 */
export function pcg32Int(rng: Pcg32, bound: number): number {
  if (bound <= 0) return 0;
  const threshold = (0x100000000 - bound) % bound;
  for (;;) {
    const r = pcg32Next(rng);
    if (r >= threshold) return r % bound;
  }
}

/**
 * Standard normal, via Marsaglia polar. Drives the pass/shot error models (§4.2).
 *
 * Deliberately stateless: the classic implementation caches the second variate
 * for the next call, but that cache would be hidden state outside WorldState —
 * it would not clone, would not hash, and would desync reconciliation exactly
 * the way §15.3 describes. We spend the extra draw and stay pure.
 */
export function pcg32Gaussian(rng: Pcg32): number {
  let u: number;
  let v: number;
  let s: number;
  do {
    u = pcg32Float(rng) * 2 - 1;
    v = pcg32Float(rng) * 2 - 1;
    s = u * u + v * v;
  } while (s >= 1 || s === 0);
  return u * Math.sqrt((-2 * Math.log(s)) / s);
}

/** Gaussian clamped to ±`sigmas`, so a freak draw can't produce an absurd shot. */
export function pcg32GaussianClamped(rng: Pcg32, sigmas = 3): number {
  const g = pcg32Gaussian(rng);
  return g < -sigmas ? -sigmas : g > sigmas ? sigmas : g;
}

/** Copy state into `dst`. Zero allocation — for cloneWorld(). */
export function pcg32CopyInto(dst: Pcg32, src: Pcg32): void {
  dst.stateHi = src.stateHi;
  dst.stateLo = src.stateLo;
  dst.incHi = src.incHi;
  dst.incLo = src.incLo;
}

/** Default-seeded generator matching the reference implementation's stream. */
export function pcg32Default(): Pcg32 {
  return { stateHi: 0, stateLo: 0, incHi: DEFAULT_INC_HI, incLo: DEFAULT_INC_LO };
}
